import React, { createContext, useState, useEffect, useContext } from "react";
import {
  saveOpportunity,
  unsaveOpportunity,
  getSavedOpportunities,
  isOpportunitySaved,
} from "../services/parseService";
import { useAuth } from "./AuthContext";
import { Opportunity } from "../types";

interface FavoritesContextProps {
  savedOpportunities: Opportunity[];
  isLoading: boolean;
  saveOpportunity: (opportunityId: string) => Promise<void>;
  unsaveOpportunity: (opportunityId: string) => Promise<void>;
  isSaved: (opportunityId: string) => boolean;
  refresh: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextProps>({
  savedOpportunities: [],
  isLoading: false,
  saveOpportunity: async () => {},
  unsaveOpportunity: async () => {},
  isSaved: () => false,
  refresh: async () => {},
});

export const useFavorites = () => useContext(FavoritesContext);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [savedOpportunities, setSavedOpportunities] = useState<Opportunity[]>(
    [],
  );
  const [savedOpportunityIds, setSavedOpportunityIds] = useState<Set<string>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, currentUser } = useAuth();

  // Load saved opportunities when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      loadSavedOpportunities();
    } else {
      // Clear saved opportunities when not authenticated
      setSavedOpportunities([]);
      setSavedOpportunityIds(new Set());
    }
  }, [isAuthenticated, currentUser]);

  const loadSavedOpportunities = async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const opportunities = await getSavedOpportunities();

      // Convert Parse objects to regular objects if needed
      const parsedOpportunities = opportunities
        .map((opp: any) => {
          // Make sure opp is defined before accessing properties
          if (!opp) return null;

          // If it's a Parse Object, get its attributes
          if (opp.attributes) {
            // Get the id and attributes from the Parse object
            const id = opp.id;
            const attributes = { ...opp.attributes };

            // Make sure dates are processed properly
            const parseDate = (dateString: string | Date) => {
              if (dateString instanceof Date) return dateString.toISOString();
              return dateString;
            };

            // Return a properly formatted opportunity object
            return {
              id,
              ...attributes,
              startDate: parseDate(attributes.startDate),
              endDate: parseDate(attributes.endDate),
              deadline: attributes.deadline
                ? parseDate(attributes.deadline)
                : null,
            };
          }
          return opp;
        })
        .filter(Boolean); // Remove any null items

      console.log(`Loaded ${parsedOpportunities.length} saved opportunities`);
      setSavedOpportunities(parsedOpportunities);

      // Update the set of saved IDs for quick lookup
      const idSet: Set<string> = new Set(parsedOpportunities.map((opp: any) => opp.id as string));
      setSavedOpportunityIds(idSet);
    } catch (error) {
      console.error("Error loading saved opportunities:", error);
      
      // If it's a session expiration error, clear the saved opportunities
      if (error instanceof Error && error.message.includes('session has expired')) {
        setSavedOpportunities([]);
        setSavedOpportunityIds(new Set());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveFavorite = async (opportunityId: string) => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      await saveOpportunity(opportunityId);

      // Update local state
      if (!savedOpportunityIds.has(opportunityId)) {
        const updatedIds = new Set(savedOpportunityIds);
        updatedIds.add(opportunityId);
        setSavedOpportunityIds(updatedIds);
      }

      // Refresh the list to get the complete opportunity objects
      await loadSavedOpportunities();
    } catch (error) {
      console.error("Error saving opportunity:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const unsaveFavorite = async (opportunityId: string) => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      await unsaveOpportunity(opportunityId);

      // Update local state
      if (savedOpportunityIds.has(opportunityId)) {
        const updatedIds = new Set(savedOpportunityIds);
        updatedIds.delete(opportunityId);
        setSavedOpportunityIds(updatedIds);

        // Also update the array of saved opportunities
        setSavedOpportunities(
          savedOpportunities.filter((opp) => opp.id !== opportunityId),
        );
      }
    } catch (error) {
      console.error("Error unsaving opportunity:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isSaved = (opportunityId: string): boolean => {
    return savedOpportunityIds.has(opportunityId);
  };

  const refresh = async () => {
    await loadSavedOpportunities();
  };

  const value = {
    savedOpportunities,
    isLoading,
    saveOpportunity: saveFavorite,
    unsaveOpportunity: unsaveFavorite,
    isSaved,
    refresh,
  };

  return (
    <FavoritesContext.Provider value={value} data-oid="12hzg6.">
      {children}
    </FavoritesContext.Provider>
  );
};
